// @ts-nocheck
import { MouseEvent } from 'react';
import { flushSync } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import sunFill from '@iconify/icons-eva/sun-fill';
import moonFill from '@iconify/icons-eva/moon-fill';
import { Box, GlobalStyles, Tooltip } from '@mui/material';

import { MIconButton } from './@material-extend';
import useSettings from '../hooks/useSettings';

const themeTransitionGlobalStyles = {
  '::view-transition-old(root), ::view-transition-new(root)': {
    animation: 'none',
    mixBlendMode: 'normal',
  },
  '::view-transition-old(root)': {
    zIndex: 1,
  },
  '::view-transition-new(root)': {
    zIndex: 2,
    animation: 'theme-toggle-reveal 420ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  '@keyframes theme-toggle-reveal': {
    from: {
      clipPath:
        'circle(0px at var(--theme-toggle-x, 50%) var(--theme-toggle-y, 50%))',
    },
    to: {
      clipPath:
        'circle(var(--theme-toggle-r, 150vmax) at var(--theme-toggle-x, 50%) var(--theme-toggle-y, 50%))',
    },
  },
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export default function ThemeModeToggle() {
  const { themeMode, onChangeMode } = useSettings();
  const isLight = themeMode === 'light';
  const nextMode = isLight ? 'dark' : 'light';

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    const apply = () => onChangeMode({ target: { value: nextMode } });

    const supportsViewTransitions =
      typeof document !== 'undefined' && typeof document.startViewTransition === 'function';

    if (!supportsViewTransitions || prefersReducedMotion()) {
      apply();
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const root = document.documentElement;
    root.style.setProperty('--theme-toggle-x', `${x}px`);
    root.style.setProperty('--theme-toggle-y', `${y}px`);
    root.style.setProperty('--theme-toggle-r', `${endRadius}px`);

    document.startViewTransition(() => {
      flushSync(apply);
    });
  };

  return (
    <>
      <GlobalStyles styles={themeTransitionGlobalStyles} />
      <Tooltip title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}>
        <MIconButton
          onClick={handleClick}
          size="large"
          aria-label={`Switch to ${nextMode} mode`}
          sx={{ overflow: 'hidden' }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <AnimatePresence initial={false} mode="wait">
              <motion.span
                key={themeMode}
                initial={{ opacity: 0, rotate: -180, scale: 0.4 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 180, scale: 0.4 }}
                transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                style={{
                  display: 'inline-flex',
                  position: 'absolute',
                  inset: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon icon={isLight ? moonFill : sunFill} width={20} height={20} />
              </motion.span>
            </AnimatePresence>
          </Box>
        </MIconButton>
      </Tooltip>
    </>
  );
}
